import { axiosInstance } from '../../../api/axiosConfig';
import { warrantyApi as sharedWarrantyApi } from '@/features/warranties/api/warrantyApi';
import type { AuditLog } from '../../users/types/audit-log.types';
import type {
  Warranty,
  UpdateWarrantyDto,
} from '../types/warranty.types';

export const warrantyApi = {
  // Méthodes partagées (lecture + mutations liste)
  ...sharedWarrantyApi,

  // Méthodes locales outil-pro (lecture admin-only)

  getWarrantyByCode: async (code: string): Promise<Warranty> => {
    const { data } = await axiosInstance.get(`/warranties-admin/code/${code}`);
    return data;
  },

  getWarrantiesBySociety: async (societyId: string): Promise<Warranty[]> => {
    const { data } = await axiosInstance.get('/warranties-admin', {
      params: { societyId },
    });
    return data.warranties || [];
  },

  getInheritedWarranties: async (societyId: string): Promise<Warranty[]> => {
    const { data } = await axiosInstance.get(`/warranties-admin/inherited/${societyId}`);
    return data;
  },

  getWarrantyAuditLogs: async (uuid: string, limit = 50): Promise<AuditLog[]> => {
    const { data } = await axiosInstance.get(`/warranties-admin/${uuid}/audit-logs`, {
      params: { limit },
    });
    return data;
  },

  getAuditLogs: async (warrantyUuid: string, limit = 50): Promise<AuditLog[]> => {
    const { data } = await axiosInstance.get(`/warranties-admin/${warrantyUuid}/audit-logs`, {
      params: { limit },
    });
    return data;
  },

  getCreationLogs: async (warrantyUuid: string, limit = 100): Promise<AuditLog[]> => {
    const { data } = await axiosInstance.get(`/warranties-admin/${warrantyUuid}/creation-logs`, {
      params: { limit },
    });
    return data;
  },

  getWarrantyStats: async (uuid: string) => {
    const { data } = await axiosInstance.get(`/warranties-admin/${uuid}/stats`);
    return data;
  },

  // Méthodes locales outil-pro (mutations)
  // createWarranty → vient du spread sharedWarrantyApi

  updateWarranty: async (uuid: string, warranty: UpdateWarrantyDto): Promise<Warranty> => {
    console.log('[warrantyApi.updateWarranty] PUT /warranties-admin/' + uuid, warranty);
    const { data } = await axiosInstance.put(`/warranties-admin/${uuid}`, warranty);
    console.log('[warrantyApi.updateWarranty] Response:', data);
    return data;
  },

  deleteWarranty: async (uuid: string): Promise<void> => {
    await axiosInstance.delete(`/warranties-admin/${uuid}`);
  },

  // activateWarranty, deactivateWarranty, moveWarranty, duplicateWarranty
  // → viennent du spread sharedWarrantyApi

  broadcastWarranty: async (uuid: string, isBroadcast: boolean): Promise<Warranty> => {
    const { data } = await axiosInstance.patch(`/warranties-admin/${uuid}/broadcast`, {
      isBroadcast,
    });
    return data;
  },

  addParts: async (warrantyId: string, partIds: number[]): Promise<void> => {
    await axiosInstance.post(`/warranties-admin/${warrantyId}/parts`, { partIds });
  },

  removeParts: async (warrantyId: string, partIds: number[]): Promise<void> => {
    await axiosInstance.delete(`/warranties-admin/${warrantyId}/parts`, {
      data: { partIds },
    });
  },

  replaceParts: async (warrantyId: string, partIds: number[]): Promise<void> => {
    await axiosInstance.put(`/warranties-admin/${warrantyId}/parts`, { partIds });
  },

  validateCode: async (code: string, excludeId?: string): Promise<{ isValid: boolean }> => {
    const { data } = await axiosInstance.post('/warranties-admin/validate-code', {
      code,
      excludeId,
    });
    return data;
  },

  generateCode: async (societyCode: string): Promise<{ code: string }> => {
    const { data } = await axiosInstance.post('/warranties-admin/generate-code', {
      societyCode,
    });
    return data;
  },

  downloadWarrantyPdf: async (uuid: string): Promise<Blob> => {
    const { data } = await axiosInstance.get(`/warranties-admin/${uuid}/pdf`, {
      responseType: 'blob',
    });
    return data;
  },

  getProvisionsPreview: async (societyId: string): Promise<{ contractsCount: number }> => {
    const { data } = await axiosInstance.get(`/warranties-admin/societies/${societyId}/provisions-preview`);
    return data;
  },

  updateProvisionsForSociety: async (societyId: string): Promise<{ total: number }> => {
    const { data } = await axiosInstance.post(`/warranties-admin/societies/${societyId}/update-provisions`);
    return data;
  },

  updateProvisionsForWarranty: async (warrantyId: string): Promise<{ total: number }> => {
    const { data } = await axiosInstance.post(`/warranties-admin/${warrantyId}/update-provisions`);
    return data;
  },
};
